import type { PageBackgroundModel } from 'interfaces/database';
import type { PageBackgroundPatternResource } from 'interfaces/project';

type BackgroundPatternScaleModel = Pick<PageBackgroundModel, 'image'> & {
	multiplier?: unknown;
};

export interface BackgroundPatternRenderData {
	image: CanvasImageSource;
	scaleX: number;
	scaleY: number;
}

/**
 * Legacy-safe pattern scale used when page background metadata does not define
 * a valid multiplier.
 */
const DEFAULT_BACKGROUND_PATTERN_MULTIPLIER = 0.5;

/**
 * Selects the full source or an explicitly requested thumbnail source, then
 * compensates for the difference between source pixels and authored tile
 * dimensions.
 *
 * @param resource Loaded pattern resource with logical and render dimensions.
 * @param scale Base viewport and catalog multiplier applied to the logical tile.
 * @param optimizePreview Whether to prefer the bounded thumbnail source.
 * @returns Canvas source and independent transforms for both axes.
 */
export function getBackgroundPatternRenderData(
	resource: PageBackgroundPatternResource,
	scale: number,
	optimizePreview = false,
): BackgroundPatternRenderData {
	const preview = resource.preview;
	let source = resource.image;

	if (
		preview
		&& optimizePreview
	) {
		source = preview;
	}

	return {
		image: source.image,
		scaleX: scale * (resource.width / source.width),
		scaleY: scale * (resource.height / source.height),
	};
}

/**
 * Normalizes multiplier values returned by numeric or decimal database columns.
 *
 * @param multiplier Page-background multiplier from the API.
 * @returns A finite positive multiplier, or `null` when invalid.
 */
function normalizeBackgroundPatternMultiplier(
	multiplier: unknown,
): number | null {
	let numericMultiplier: number;

	if (typeof multiplier === 'number') {
		numericMultiplier = multiplier;
	} else if (
		typeof multiplier === 'string'
		&& multiplier.trim().length > 0
	) {
		numericMultiplier = Number(multiplier);
	} else {
		return null;
	}

	if (
		Number.isFinite(numericMultiplier)
		&& numericMultiplier > 0
	) {
		return numericMultiplier;
	}

	return null;
}

/**
 * Detects whether a background pattern source points at SVG content.
 *
 * @param source Persisted background pattern URL, path, or data URL.
 * @returns Whether the source should use SVG intrinsic-dimension normalization.
 */
export function isSVGBackgroundPatternSource(source?: string | null): boolean {
	if (!source) {
		return false;
	}

	const normalizedSource = source
		.trim()
		.toLowerCase();

	if (normalizedSource.startsWith('data:image/svg+xml')) {
		return true;
	}

	const sourceWithoutQuery = normalizedSource.split(/[?#]/)[0];

	return sourceWithoutQuery.endsWith('.svg');
}

/**
 * Returns the configured tile scale before viewport and device scaling.
 *
 * @param source Persisted background pattern URL, path, or data URL.
 * @param backgroundModels Page-background metadata available to the editor.
 * @returns Configured multiplier, or the legacy-safe fallback when unavailable.
 */
export function getBackgroundPatternScaleRatio(
	source?: string | null,
	backgroundModels: readonly BackgroundPatternScaleModel[] = [],
): number {
	const multiplier = normalizeBackgroundPatternMultiplier(
		source
			? backgroundModels.find((backgroundModel) => backgroundModel.image === source)?.multiplier
			: undefined,
	);

	return (
		multiplier
		?? DEFAULT_BACKGROUND_PATTERN_MULTIPLIER
	);
}
